/**
 * Pure utilities for `AiStructuredTable`. Ported verbatim from
 * Trusstor's `aiTable.utils.ts` (`moment` swapped for `Intl.*` /
 * native Date so d-orca doesn't pull in moment-timezone).
 *
 * Kept side-effect free so the component stays straightforward to
 * test in isolation.
 */

export type ChipVariant = 'success' | 'progress' | 'warning' | 'critical' | 'neutral';

export interface SummaryItem {
  label: string;
  count: number;
  variant: ChipVariant;
}

export interface SortConfig {
  columnKey: string;
  direction: 'asc' | 'desc';
}

/** Lowercased status string → chip variant. */
export const STATUS_MAP: Record<string, ChipVariant> = {
  'complete': 'success',
  'completed': 'success',
  'done': 'success',
  'resolved': 'success',
  'closed': 'success',
  'in progress': 'progress',
  'in-progress': 'progress',
  'active': 'progress',
  'ongoing': 'progress',
  'open': 'progress',
  'pending': 'warning',
  'not started': 'warning',
  'scheduled': 'warning',
  'planned': 'warning',
  'delayed': 'critical',
  'overdue': 'critical',
  'failed': 'critical',
  'critical': 'critical',
  'blocked': 'critical',
  'cancelled': 'neutral',
  'n/a': 'neutral',
  // The task-commit ladder, in the words the assistant's tools write
  // (backend `tools/ladder-words.ts`; AI-assistant activity model spec, D7):
  // committed is a position, not progress; "areas done" is waiting for a person.
  'committed': 'neutral',
  'committed, not started': 'neutral',
  'committed, status not confirmed': 'neutral',
  'areas done, waiting to be marked done': 'success',
  // The brief's schedule changes.
  'marked done': 'success',
  'reopened': 'warning',
  're-dated': 'warning',
  // Logistics, in the words `query_deliveries` and `query_stock` write
  // (backend `tools/deliveries.rows.ts`, `tools/stock.rows.ts`): an agreed
  // date is not a target, arrived is not synced, never counted is not empty.
  'agreed': 'success',
  'target': 'warning',
  'draft': 'neutral',
  'arrived': 'success',
  'not synced': 'warning',
  'reorder now': 'critical',
  'needs recount': 'warning',
  'not counted': 'warning',
  'untracked': 'warning',
  'ok': 'success',
};

/** String values that should render as the em-dash empty marker. */
export const EMPTY_VALUES = new Set([
  'no assignee',
  'none',
  'n/a',
  '-',
  '',
  'unassigned',
  'not assigned',
]);

/** Show the search input when row count meets/exceeds this. */
export const SEARCH_THRESHOLD = 5;

export function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  return EMPTY_VALUES.has(String(value).toLowerCase().trim());
}

export function getChipVariant(value: string): ChipVariant {
  return STATUS_MAP[value.toLowerCase().trim()] ?? 'neutral';
}

/**
 * Locale-aware date formatter cache. CLAUDE.md mandates JP date formatting
 * for the deployed product; rather than hard-coding `'en'` (the original
 * Trusstor port), we look up the per-locale formatter so a `ja` user sees
 * `2026年5月13日` and an `en` user sees `13 May 2026`. The cache is keyed
 * by the locale tag so we don't re-construct the formatter on every cell
 * render. JST timezone is implicit — we don't pass `timeZone` because the
 * backend already serialises absolute timestamps.
 */
const DATE_FORMATTERS: Record<string, Intl.DateTimeFormat> = {};

function getDateFormatter(locale: string): Intl.DateTimeFormat {
  let cached = DATE_FORMATTERS[locale];
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    DATE_FORMATTERS[locale] = cached;
  }
  return cached;
}

/**
 * Best-effort date formatter for AI-emitted cells. Trusstor's tools
 * emit dates in any of several formats; we try ISO first then a couple
 * of dotted/slashed forms. Returns the raw value when none parse.
 *
 * Pass the active i18n locale (e.g. `'ja'`) so dates render in the
 * user's language; defaults to `'en'` for callers that don't have a
 * locale handy (tests, unit-tested helpers).
 */
export function formatDate(value: string, locale = 'en'): string {
  if (!value) return value;
  const formatter = getDateFormatter(locale);
  // ISO 8601 covers `YYYY-MM-DD`, `YYYY-MM-DDTHH:mm:ss`, and offset
  // variants. Try direct construction first.
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) {
    return formatter.format(direct);
  }
  // Fall through to dotted / slashed `DD.MM.YYYY` and `DD/MM/YYYY`.
  const dotted = /^(\d{2})[./](\d{2})[./](\d{4})$/.exec(value);
  if (dotted) {
    const [, d, m, y] = dotted;
    const parsed = new Date(`${y}-${m}-${d}T00:00:00Z`);
    if (!Number.isNaN(parsed.getTime())) return formatter.format(parsed);
  }
  return value;
}

export function extractNumber(str: string): number | null {
  const match = /\d+/.exec(str);
  return match ? parseInt(match[0], 10) : null;
}

/**
 * Compare two cell values for `Array.sort`. Numeric-first (parses both
 * as floats), then "embedded number" (sort `Floor 9` before `Floor 10`),
 * then locale-aware string compare. `direction` flips the sign.
 */
export function compareValues(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  const multiplier = direction === 'asc' ? 1 : -1;
  const strA = String(a ?? '');
  const strB = String(b ?? '');
  const numA = parseFloat(strA);
  const numB = parseFloat(strB);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
    return (numA - numB) * multiplier;
  }
  const embeddedA = extractNumber(strA);
  const embeddedB = extractNumber(strB);
  if (embeddedA !== null && embeddedB !== null) {
    return (embeddedA - embeddedB) * multiplier;
  }
  return strA.localeCompare(strB) * multiplier;
}
